// src/contexts/AdminOverrideContext.js
//
// Provides the global admin override state to the entire application.
// The GET endpoint is intentionally accessible to unauthenticated users so
// that the override (Mode 1: Unlock Everything) also applies to anonymous
// visitors.  Only the PATCH endpoint is admin-restricted (enforced on the
// backend by IsAdminUser).
//
// Override flow:
//   AdminOverrideProvider fetches /api/admin/global-override/ on mount,
//   on tab focus (visibility-change), and after every mutation.
//   SubscriptionContext consumes this context and replaces the real
//   subscription snapshot with a synthetic Pro snapshot when the override
//   is active.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../api';

// ─── Constants ────────────────────────────────────────────────────────────────

export const OVERRIDE_MODES = {
  /** Normal operation — no override in effect. */
  NORMAL: 'normal',
  /** Every visitor sees all content as if all gates are removed. */
  UNLOCK_ALL: 'unlock_all',
  /** Every visitor behaves as an active Pro subscriber. */
  FORCE_PRO: 'force_pro',
};

const OVERRIDE_API_URL = 'admin/global-override/';

// Only re-fetch on visibility-change if the last fetch was older than this.
const STALE_THRESHOLD_MS = 60 * 1000; // 1 minute

// ─── Default / safe state (override off) ─────────────────────────────────────

const DEFAULT_OVERRIDE_STATE = {
  overrideEnabled: false,
  overrideMode: OVERRIDE_MODES.NORMAL,
  lastModified: null,
  modifiedBy: null,
};

// ─── Context ──────────────────────────────────────────────────────────────────

const AdminOverrideContext = createContext({
  ...DEFAULT_OVERRIDE_STATE,
  loading: true,
  fetchOverride: () => {},
  setOverride: async () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AdminOverrideProvider({ children }) {
  const [overrideState, setOverrideState] = useState(DEFAULT_OVERRIDE_STATE);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);

  /**
   * Normalise the raw API response into our internal shape.
   * Defaults to "override off" on any unexpected payload so a backend error
   * never accidentally unlocks content.
   */
  const normaliseResponse = (data) => ({
    overrideEnabled: Boolean(data?.override_enabled),
    overrideMode: Object.values(OVERRIDE_MODES).includes(data?.override_mode)
      ? data.override_mode
      : OVERRIDE_MODES.NORMAL,
    // Backend may call this field last_modified, updated_at, or modified_at.
    lastModified:
      data?.last_modified ??
      data?.updated_at ??
      data?.modified_at ??
      null,
    // Backend may return modified_by as a nested object {email:...}, a raw
    // email string (modified_by_email), or not at all.
    modifiedBy:
      data?.modified_by_email ??
      data?.modified_by?.email ??
      null,
  });

  const fetchOverride = useCallback(async () => {
    try {
      const res = await api.get(OVERRIDE_API_URL);
      setOverrideState(normaliseResponse(res.data));
      lastFetchRef.current = Date.now();
    } catch (err) {
      // Fail-safe: on any error, keep override disabled.
      // An unavailable backend should never accidentally unlock premium content.
      console.warn('[AdminOverride] Failed to fetch override state:', err.message);
      setOverrideState(DEFAULT_OVERRIDE_STATE);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount.
  useEffect(() => {
    fetchOverride();
  }, [fetchOverride]);

  // Re-fetch on tab focus (same pattern as subscription status re-sync).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const stale = Date.now() - lastFetchRef.current > STALE_THRESHOLD_MS;
        if (stale) fetchOverride();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchOverride]);

  /**
   * Admin-only mutation.  Sends PATCH to the backend (which enforces
   * IsAdminUser) then updates local state from the response.
   *
   * @param {{ enabled: boolean, mode: string }} options
   */
  const setOverride = useCallback(async ({ enabled, mode }) => {
    const res = await api.patch(OVERRIDE_API_URL, {
      override_enabled: Boolean(enabled),
      override_mode: mode,
    });
    setOverrideState(normaliseResponse(res.data));
    lastFetchRef.current = Date.now();
  }, []);

  const value = useMemo(
    () => ({
      ...overrideState,
      loading,
      fetchOverride,
      setOverride,
    }),
    [overrideState, loading, fetchOverride, setOverride],
  );

  return (
    <AdminOverrideContext.Provider value={value}>
      {children}
    </AdminOverrideContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAdminOverride() {
  return useContext(AdminOverrideContext);
}
